import { useEffect, useState } from 'react'
import { useAlert } from 'react-alert'
import { useTranslation } from 'react-i18next'

import { getApi } from '../../../../../api'
import { LICENSE_FIELDS } from '../../../../../config/constants'
import endpoints from '../../../../../config/endpoints'
import type { Package } from '../../../../../types/license'
import Form from '../../../../../ui/Form/Form'

import type { LicenseFormProps } from './TenantLicensesForm.types'

function TenantLicenseForm(props: LicenseFormProps) {
  const {
    initialValues,
    titleKey,
    disabled,
    isEditMode,
    isLicenseActive,
    isPastDate,
    onSubmit,
    onCancel,
    onClose,
  } = props
  const { t } = useTranslation()
  const alert = useAlert()

  const [licensePackages, setLicensePackages] = useState<Package[]>([])
  const [loadingPackages, setLoadingPackages] = useState(true)
  const [formdata, setFormdata] = useState(initialValues)
  const [hasFocus, setHasFocus] = useState(
    initialValues?.license_package !== ''
  )

  useEffect(() => {
    if (!hasFocus) {
      setLoadingPackages(false)

      return
    }

    getApi<Package[]>({ endpoint: endpoints.packages() })
      .then((res) => {
        setLicensePackages(res)
        const packageValue = initialValues?.package || ''
        setFormdata((prev) => ({
          ...prev,
          package: packageValue,
        }))
      })
      .catch((err) => alert.error(err.message))
      .finally(() => setLoadingPackages(false))
  }, [hasFocus])

  if (loadingPackages) {
    return null
  }

  return (
    <Form
      fields={[
        {
          name: LICENSE_FIELDS.PACKAGE,
          label: t('Tenants.Profile.Licenses.Form.Fields.PackageSelect'),
          type: 'select',
          options: licensePackages,
          onFocus: () => setHasFocus(true),
          isLoading: loadingPackages,
        },
        {
          name: LICENSE_FIELDS.START_DATE,
          label: t('Tenants.Profile.Licenses.Form.Fields.StartDate'),
          type: 'date',
          disabled: isEditMode && isLicenseActive && isPastDate,
        },
        {
          name: LICENSE_FIELDS.END_DATE,
          label: t('Tenants.Profile.Licenses.Form.Fields.EndDate'),
          type: 'date',
        },
        ...(!isEditMode
          ? [
              {
                name: LICENSE_FIELDS.REVOKE_OLD_LICENSE,
                label: t(
                  'Tenants.Profile.Licenses.Form.Fields.RevokeOldLicense'
                ),
                type: 'checkbox' as const,
              },
            ]
          : []),
      ]}
      initialValues={formdata}
      title={t(titleKey)}
      disabled={disabled}
      isEditMode={isEditMode}
      onSubmit={onSubmit}
      onCancel={onCancel}
      onClose={onClose}
    />
  )
}

export default TenantLicenseForm
